<template>
  <create-account-layout
    :current-step="1"
    title="Carrier Details"
  >
    <section>
      <p class="mb-6 mt-8 text-left text-sm text-dark-gray">
        Find your carrier. If you're a dispatch service, start with one for now,
        and you'll have the opportunity to add more later.
      </p>
      <div class="mt-4 grid grid-cols-12">
        <div class="col-span-4 flex justify-start md:col-span-5">
          <toggle-button
            :items="toggle_items"
            :active-toggle-bg-yellow="true"
            @selected-value="updateCarrierType"
          />
        </div>
        <div class="col-span-8 flex flex-col justify-start pt-1 md:col-span-7">
          <form-field
            id="carrier_number"
            ref="formField"
            :value="form.carrier_number"
            :placeholder="fieldPlaceholder"
            :on-input="onInput"
            name="carrier_number"
            class="text-dark-blue"
            maxlength="8"
            :is-valid="!carrier_number_error"
            error-message="You have entered an invalid DOT or MC number. Please try again."
            :search-icon="true"
            @keyup.enter="
              carrier_number_error || !form?.carrier_number
                ? () => {}
                : getCarrier()
            "
          />
        </div>
      </div>
      <button-main
        class="mb-4"
        variant="secondary"
        :disabled="carrier_number_error || !form?.carrier_number"
        :is-loading="loading"
        @click="getCarrier"
      >
        Search
      </button-main>
    </section>
    <section v-if="selected_carrier">
      <h2 class="mt-6 text-left text-xl text-dark-blue">Your Company</h2>
      <div
        class="my-4 grid grid-cols-12 gap-1 rounded-md border-2 border-yellow-500 p-4 shadow-lg"
      >
        <div class="col-span-12 text-left text-dark-gray">
          {{ selected_carrier.legal_name }}
        </div>
        <div class="col-span-6 text-left text-body-color">
          {{ identifiers }}
        </div>
      </div>
    </section>
    <section v-if="!selected_carrier && request_launched && !loading">
      <div class="my-3 rounded-md bg-off-white p-4">
        <small class="text-left text-body-color">
          Carrier could not be found. Try again.
        </small>
      </div>
    </section>
    <div class="flex flex-col">
      <button-main
        class="my-3 mb-4"
        :disabled="!selected_carrier || carrier_number_error"
        :is-loading="loadingContinueBtn"
        value="Continue"
        @click="handleSubmit"
      >
        Continue
      </button-main>
      <section v-if="!request_launched && !selected_carrier && !loading">
        <div class="mt-3 flex flex-col items-center">
          <p class="mb-2 text-1md text-body-color">
            Don't have a DOT or MC number?
          </p>
          <p
            class="flex cursor-pointer items-center text-1md text-yellow-500"
            @click="visible = true"
          >
            Continue Here
          </p>
          <!-- <router-link
            class="text-yellow-500 flex items-center text-1md"
              :to="{ name: 'CarrierType' }"
            >
              Continue Here
            </router-link> -->
          <!-- TODO: Once the endpoint is built change the link (<a>) for the button below -->
          <!-- 
              <button-main
                @click="openContactUsModal"
                size="md"
                class="text-yellow-500 flex items-center text-sm"
                variant="link"
              >
                Contact Us
              </button-main> 
            -->
        </div>
      </section>
    </div>
    <confirm-delete-modal
      v-if="confirmDelete"
      :visible="confirmDelete"
      @close="onCloseDeleteModal"
      @delete="handleDeleteContact"
    />
    <eligibility-confirmation-modal
      :visible="visible"
      @close="() => (visible = false)"
      @continue="redirectToCarrierTypePage()"
    />
  </create-account-layout>
  <contact-us-modal
    v-if="showContactUsModal"
    :visible="showContactUsModal"
    @close="onCloseContactUsModal"
  />
</template>

<script>
import FormField from '@/components/FormField.vue';
import ButtonMain from '@/components/Button.vue';
import ToggleButton from '@/components/ToggleButton.vue';
import CreateAccountLayout from '@/components/layouts/CreateAccountLayout.vue';
import { toastResponse } from '@/common/handleServerResponse';
import contactUs from '@/mixins/contactUs';
import EligibilityConfirmationModal from '@/components/modals/EligibilityConfirmationModal.vue';

export default {
  name: 'CarrierDetails',
  components: {
    CreateAccountLayout,
    FormField,
    ButtonMain,
    ToggleButton,
    EligibilityConfirmationModal,
  },
  mixins: [contactUs],
  data() {
    return {
      form: {
        carrier_number: '',
        carrier_type: 'mc',
      },
      support_email: 'service@highway.com',
      selected_carrier: null,
      request_launched: false,
      carrier_number_error: false,
      toggle_items: ['mc', 'dot'],
      loading: false,
      loadingContinueBtn: false,
      confirmDelete: false,
      visible: false,
    };
  },
  computed: {
    fieldPlaceholder() {
      return this.form.carrier_type === 'dot' ? 'Enter DOT #' : 'Enter MC #';
    },
    identifiers() {
      const allowedIdentifiers = this.selected_carrier.identifiers.filter(
        (identifier) => {
          return identifier.is_type === 'MC' || identifier.is_type === 'DOT';
        }
      );
      const identifiersList = allowedIdentifiers.map((identifier) => {
        return `${identifier.is_type}# ${identifier.value}`;
      });
      return identifiersList.join(' - ');
    },
    shouldShowPrivacyPolicy() {
      return (
        this.isPrivacyPolicyStepEnabled && !this.isPrivacyPolicyConsentedAt
      );
    },
    isPrivacyPolicyStepEnabled() {
      return this.$store.getters['users/getFeatureFlag']?.(
        'privacy_policy_step_enabled'
      )?.['enabled'];
    },
    isPrivacyPolicyConsentedAt() {
      return (
        this.$store.state.users.currentUser?.privacy_policy_consented_at
          ?.length > 0 || false
      );
    },
  },
  async mounted() {
    await this.$store.dispatch('users/refreshCurrentUser');
    let currentUser = this.$store.state.users.currentUser;
    // Redirect if the user is already associated with a company
    console.log('checking if already a user with a claimed company');
    if (currentUser?.company?.id && !this.shouldShowPrivacyPolicy) {
      console.log('user is with a claimed company, re-route the user');
      this.$router.push({ name: 'Home' });
    }
  },
  methods: {
    redirectToCarrierTypePage() {
      this.$router.push({ name: 'CarrierType' });
    },
    onInput(e) {
      const numbers = /^[0-9]+$/;
      this.form.carrier_number = e.target.value;
      if (
        this.form.carrier_number.length &&
        !this.form.carrier_number.match(numbers)
      ) {
        this.carrier_number_error = true;
      } else {
        this.carrier_number_error = false;
      }
    },
    updateCarrierType(type) {
      this.form.carrier_type = type;
    },
    async getCarrier() {
      this.selected_carrier = null;
      this.loading = true;
      try {
        const res = await this.$store.dispatch(
          'carriers/getCarriersByIdentifier',
          {
            is_type: this.form.carrier_type?.toUpperCase(),
            value: this.form.carrier_number,
          }
        );

        if ([200, 201].includes(res.status)) {
          if (res.data) {
            this.selected_carrier = res.data;
          }
        } else if (res.status !== 404) {
          toastResponse(this, res, null);
        }
        this.request_launched = true;
      } catch {
        this.selected_carrier = null;
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit() {
      let currentUser = this.$store.state.users.currentUser;
      this.loadingContinueBtn = true;
      try {
        this.$store.commit('updateSignupField', {
          name: 'manualCarrier',
          value: undefined,
        });
        console.log('handleSubmit', currentUser);
        if (
          currentUser &&
          currentUser.company &&
          currentUser.company.company_type == 'broker'
        ) {
          console.log('handleSubmit for a logged in broker user');
          console.log(this.selected_carrier);
          // update the requested_carrier_id
          const res = await this.$store.dispatch(
            'users/setRequestedCarrierId',
            {
              requested_carrier_id: this.selected_carrier.id,
              user_id: currentUser.id,
            }
          );

          if ([200, 201].includes(res.status)) {
            return await this.$router.push({
              name: 'FMSCAAccountVerification',
            });
          } else if (res.status !== 404) {
            toastResponse(this, res, null);
          }
        } else {
          await this.$router.push({
            name: 'CreateAccountOrLogin',
          });
        }
      } catch (error) {
        console.error('ERROR:', error);
      } finally {
        this.loadingContinueBtn = false;
      }
    },
  },
};
</script>

<style scoped>
.button {
  @apply my-3 mb-4 bg-light-gray !important;
}
</style>
